import {
  Button,
  Divider,
  Drawer,
  Flex,
  Input,
  Popconfirm,
  Select,
  Tag,
  Typography,
} from 'antd';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { CiSearch } from 'react-icons/ci';
import { HiOutlineTrash } from 'react-icons/hi';
import { useParams } from 'react-router-dom';
import { TASk_TABS } from '../../constants';
import { allowEdit, isManager } from '../../helper';
import useUsers from '../../hooks/useUsers';
import { getUsers } from '../../redux/actions/auth';
import { sendStoreAssignedEmail } from '../../redux/actions/email';
import { addUsersToRestaurant, deleteUsersFromRestaurant } from '../../redux/actions/restaurant';
import { getTasks } from '../../redux/actions/tasks';
import RolePill from '../RolePill';
import UserAvatar from '../UserAvatar';
import { decodeId } from '../../helper/hashids';

const { Option } = Select;

const UserRow = ({ user, extra }) => {
  const fullName = `${user.first_name} ${user.last_name ?? ''}`.trim();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Flex
      align="center"
      gap={12}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '12px 12px',
        margin: '0 -12px',
        borderRadius: 8,
        background: isHovered ? '#f8fafc' : 'transparent',
        transition: 'all 0.2s ease',
        borderBottom: '1px solid #f1f5f9',
        cursor: 'default'
      }}
    >
      <UserAvatar size={40} user={user} />
      <Flex vertical style={{ flex: 1, minWidth: 0 }}>
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Text strong style={{ fontSize: 14, lineHeight: 1.4 }} ellipsis>
            {fullName}
          </Typography.Text>
          <RolePill role={user?.role} />
        </Flex>
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, lineHeight: 1.4 }}
          ellipsis
        >
          {user.email}
        </Typography.Text>
      </Flex>
      <div style={{ opacity: isHovered ? 1 : 0.6, transition: 'opacity 0.2s' }}>
        {extra}
      </div>
    </Flex>
  );
};

const UserSettingsModal = ({ open, onClose, users, restaurant, activeTab }) => {
  const { id: restaurantId } = useParams();
  const id = decodeId(restaurantId);
  const { users: globalUsers } = useUsers();
  const [searchUsers, setSearchUsers] = useState(globalUsers);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState(users ?? []);

  useEffect(() => {
    setFilteredUsers(users ?? []);
  }, [users]);

  const handleUserSearch = (e) => {
    const query = e.target.value.toLowerCase().trim();
    if (!query) {
      setFilteredUsers(users);
      return;
    }
    const filtered = users?.filter((user) => {
      const fullName = `${user.first_name} ${user.last_name ?? ''}`.toLowerCase();
      const email = user.email.toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });
    setFilteredUsers(filtered);
  };

  const addUser = async () => {
    const body = { restaurant_id: id, user_ids: selected };
    const res = await addUsersToRestaurant(body);
    if (res?.ack) {
      if (selected?.length)
        sendStoreAssignedEmail({ userIds: selected, restaurantIds: [id], type: 'RESTAURANT' });
      setSelected([]);
      getUsers();
    }
  };

  const deleteUser = async (user_id) => {
    const body = { restaurant_id: id, user_id };
    const res = await deleteUsersFromRestaurant(body);
    if (res?.ack) {
      getUsers();
      activeTab === TASk_TABS.PENDING &&
        getTasks({ id: restaurant.id, filter: TASk_TABS.PENDING });
    }
  };

  const handleSearch = useCallback(
    debounce(async (value) => {
      setLoading(true);
      if (value) {
        const res = await getUsers({ searchText: value, loading: false });
        if (res?.ack) setSearchUsers(res?.users);
      } else {
        setSearchUsers(globalUsers);
      }
      setLoading(false);
    }, 300),
    [globalUsers]
  );

  const availableUsers = searchUsers?.filter((globalUser) => {
    const isAlreadyAdded = users?.some((localUser) => localUser?.id === globalUser?.id);
    const isRoleExcluded = isManager() && (globalUser.role === 1 || globalUser.role === 2);
    return !isAlreadyAdded && !isRoleExcluded;
  });

  const userRender = (props) => {
    const { label, value, closable, onClose } = props;
    const user = globalUsers.find((u) => u.id === value);
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        color="blue"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 6px' }}
      >
        <UserAvatar size={16} user={user} />
        <Typography.Text strong style={{ fontSize: 12, color: 'inherit' }}>
          {label}
        </Typography.Text>
      </Tag>
    );
  };

  return (
    <Drawer
      title='Manage Site Users'
      placement="right"
      width={480}
      onClose={onClose}
      open={open}
      styles={{
        body: {
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }
      }}
    >
      <Flex vertical gap={12} style={{ flexShrink: 0 }}>
        <Typography.Text strong style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Add Member
        </Typography.Text>
        <Flex gap={8}>
          <Select
            size="large"
            mode="multiple"
            placeholder="Search users..."
            value={selected}
            optionLabelProp="label"
            style={{ flex: 1 }}
            tagRender={userRender}
            onSearch={handleSearch}
            filterOption={false}
            onChange={(val) => setSelected(val)}
            loading={loading}
            maxTagCount="responsive"
          >
            {availableUsers?.map((user) => (
              <Option key={user.id} value={user.id} label={`${user?.first_name} ${user?.last_name ?? ''}`}>
                <Flex align="center" gap={10} style={{ padding: '4px 0' }}>
                  <UserAvatar size={32} user={user} />
                  <Flex vertical style={{ minWidth: 0 }}>
                    <Flex align="center" gap={6}>
                      <Typography.Text strong style={{ fontSize: 13 }}>{`${user?.first_name} ${user?.last_name ?? ''}`}</Typography.Text>
                      <RolePill role={user.role} />
                    </Flex>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>{user?.email}</Typography.Text>
                  </Flex>
                </Flex>
              </Option>
            ))}
          </Select>
          <Button type="primary" size="large" disabled={!selected.length} onClick={addUser} style={{ fontWeight: 600 }}>
            Add
          </Button>
        </Flex>
      </Flex>

      <Divider style={{ margin: '20px 0' }} />

      <Flex align="center" justify="space-between" style={{ marginBottom: 12, flexShrink: 0 }}>
        <Typography.Title level={5} style={{ margin: 0, fontSize: 15 }}>
          Added Users ({filteredUsers?.length})
        </Typography.Title>
        <Input
          size='large'
          placeholder="Search..."
          allowClear
          prefix={<CiSearch />}
          onChange={handleUserSearch}
          style={{ width: 200, borderRadius: 6 }}
        />
      </Flex>

      <Flex
        vertical
        style={{
          flex: 1,
          overflowY: 'auto',
          marginRight: -12,
          paddingRight: 12
        }}
      >
        {filteredUsers?.length ? (
          filteredUsers.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              extra={
                <Popconfirm
                  title="Remove user?"
                  onConfirm={() => deleteUser(user.id)}
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    variant='filled'
                    color="danger"
                    danger
                    disabled={allowEdit(user?.role)}
                    icon={<HiOutlineTrash style={{ fontSize: 16 }} />}
                  />
                </Popconfirm>
              }
            />
          ))
        ) : (
          <Flex vertical align="center" justify="center" style={{ flex: 1, color: '#94a3b8' }}>
            <Typography.Text type="secondary">No members found</Typography.Text>
          </Flex>
        )}
      </Flex>
    </Drawer>
  );
};

export default UserSettingsModal;
